import React, { useState } from 'react';
import { Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Divider } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { startMaintenanceSequence, startTestSequence } from '../../state/actions/machine';
import { padding } from '../../theme';
import { TextTitle } from '../../components/atoms/text/titles';
import { StoreState } from '../../state/configureStore';

interface TestSequenceProps {
  SwSerialId: string;
  canRunTestSequence: boolean;
}

interface TestSequenceState {
  loading: boolean;
  error: string | null;
  successMachineId: string | null;
}

interface MaintenanceSequenceState {
  loading: boolean;
  error: string | null;
  successMachineId: string | null;
}

const TestSequence: React.FC<TestSequenceProps> = ({ SwSerialId, canRunTestSequence }) => {
  const dispatch = useDispatch();
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const testSequenceState = useSelector((state: StoreState) => state.test_sequence as TestSequenceState);

  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState<boolean>(false)
  const maintenanceSequenceState = useSelector((state: StoreState) => state.maintenance_sequence as MaintenanceSequenceState);

  const handleStartTest = () => {
    try {
      dispatch(startTestSequence(SwSerialId));
      setIsTestModalOpen(false);
    } catch (err) {
      console.error('Failed to start test sequence:', err);
    }
  };

  const handleStartMaintenance = () => {
    try {
      dispatch(startMaintenanceSequence(SwSerialId));
      setIsMaintenanceModalOpen(false);
    } catch (err) {
      console.error('Failed to start maintenance sequence:', err);
    }
  };

  return (
    <Box sx={{ padding: padding }}>
      <Box sx={{ paddingBottom: padding }}>
        <Typography variant="h6" gutterBottom>
          Start Test Sequence
        </Typography>
        <Typography variant="body1" gutterBottom>
          Click the button below to start a test sequence for this machine.
        </Typography>
        
        {testSequenceState.error && (
          <Typography color="error" sx={{ mt: 1, mb: 1 }}>
            Failed to start test sequence, please try again.
          </Typography>
        )}
        
        {testSequenceState.successMachineId === SwSerialId && (
          <Typography color="success.main" sx={{ mt: 1, mb: 1 }}>
            Test sequence started successfully!
          </Typography>
        )}

        {!canRunTestSequence && (
          <Typography color="error" sx={{ mt: 1, mb: 1 }}>
            This hub is not allowed to run test sequence. Only Brunngård hubs are allowed to run this test sequence.
          </Typography>
        )}
        
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsTestModalOpen(true)}
            disabled={testSequenceState.loading || !SwSerialId || !canRunTestSequence}
          >
            Start
          </Button>
        </Box>
      </Box>

      <Divider/>

      <Box sx={{ paddingTop: padding}}>
        <Typography variant="h6" gutterBottom>
          Start Maintenance Sequence
        </Typography>
        <Typography variant="body1" gutterBottom>
          Click the button below to start a maintenance sequence for this machine.
        </Typography>
        
        {maintenanceSequenceState.error && (
          <Typography color="error" sx={{ mt: 1, mb: 1 }}>
            Failed to start maintenance sequence, please try again.
          </Typography>
        )}
        
        {maintenanceSequenceState.successMachineId === SwSerialId && (
          <Typography color="success.main" sx={{ mt: 1, mb: 1 }}>
            Maintenance sequence started successfully!
          </Typography>
        )}
        
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsMaintenanceModalOpen(true)}
            disabled={maintenanceSequenceState.loading || !SwSerialId}
          >
            Start
          </Button>
        </Box>
      </Box>


      <Dialog 
        open={isMaintenanceModalOpen} 
        onClose={() => setIsMaintenanceModalOpen(false)} 
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            boxShadow: 'rgb(113 122 131 / 11%) 0px 7px 30px 0px',
          },
        }}
      >
        <DialogTitle>
          <TextTitle title="Confirm Maintenance Sequence" noPadding />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
          <Typography variant="body1" paragraph>
            The machine will run maintenance for 5 minutes. 
            </Typography>
            <Typography variant="body1" fontWeight="bold">Procedure:</Typography>
            <Typography variant="body1"> Textile <br />
            wait 50 seconds <br />
          </Typography>
          <br/>
            <Typography variant="body1" color="#ff0000"fontWeight="bold">Important!</Typography>
            <Typography variant="body1" paragraph> Make sure the machine is not in use and online before starting the procedure. During this time, do not use the machine. </Typography>
          <Typography variant="body1" paragraph>
            Do you want to proceed with starting the maintenance sequence?
          </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setIsMaintenanceModalOpen(false)} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleStartMaintenance} 
            variant="contained" 
            color="primary"
            disabled={maintenanceSequenceState.loading}
          >
            {maintenanceSequenceState.loading ? 'Starting...' : 'Start'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={isTestModalOpen} 
        onClose={() => setIsTestModalOpen(false)} 
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            boxShadow: 'rgb(113 122 131 / 11%) 0px 7px 30px 0px',
          },
        }}
      >
        <DialogTitle>
          <TextTitle title="Confirm Test Sequence" noPadding />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
          <Typography variant="body1" paragraph>
            The machine will run a test for 10 minutes. 
            </Typography>
            <Typography variant="body1" fontWeight="bold">Procedure:</Typography>
            <Typography variant="body1"> Leather <br />
            wait 90 seconds <br />
            Suede <br />
            wait 90 seconds <br />
            Synthetic<br />
            wait 90 seconds <br />
            Textile. <br />
          </Typography>
          <br/>
            <Typography variant="body1" color="#ff0000"fontWeight="bold">Important!</Typography>
            <Typography variant="body1" paragraph> During this time, do not use the machine. </Typography>
          <Typography variant="body1" paragraph>
            Do you want to proceed with starting the test sequence?
          </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setIsTestModalOpen(false)} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleStartTest} 
            variant="contained" 
            color="primary"
            disabled={testSequenceState.loading}
          >
            {testSequenceState.loading ? 'Starting...' : 'Start Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestSequence;
